/**
 * @file いちごスタディの面を切り替える上部バー。
 *
 * @what: 「計画 / 見取り図 / うんどう / おんがく / 教材 / 記録 / 時間と体験 / よむ / 本だな / 分析」の
 *   10 面 (studyViews.ts) を切り替える。画面の上に貼り付いて残り、広い画面では下線タブを並べ、
 *   狭い画面ではいま見ている面のボタン 1 つに畳んで、押すと 10 面のメニューを開く。
 * @why: 6 つの節を縦に積むと、いま何を見ているのか分からず目的の場所に届かない
 *   (代表 2026-08-22)。面が 10 に増えて、スマートフォンではタブが 3 段に折り返して
 *   場所を取り、下までスクロールすると切り替えに戻れなくなった (代表 2026-08-28)。
 *   ステップのタブ (StepTabs) とは別階層なので、広い画面の見た目は下線タブのままにして
 *   「面の切り替え」と「ステップの切り替え」を混ぜない。
 * @failure: 描画のみ。メニューは Esc と外側の操作で閉じ、閉じたらボタンへ焦点を戻す。
 */

import { useEffect, useRef, useState } from "react";
import { STUDY_VIEWS, type StudyView, type StudyViewId } from "./studyViews";
import "./study.css";

export function StudyTabs({
  selectedId,
  onSelect,
  views = STUDY_VIEWS,
}: {
  selectedId: StudyViewId;
  onSelect: (id: StudyViewId) => void;
  /** 並べる面。省略 = 全面。おとなの学習者では子ども向けの面を渡さない (呼び出し側で絞る)。 */
  views?: readonly StudyView[];
}) {
  const [open, setOpen] = useState(false);
  const compactRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const selected = views.find((v) => v.id === selectedId) ?? views[0] ?? STUDY_VIEWS[0]!;

  // 開いたら最初の項目へ焦点を移す。Esc と外側の操作で閉じ、↑↓ で項目を移動する
  // (ヘッダーの AccountMenu と同じ作法)。
  useEffect(() => {
    if (!open) return;
    const items = () =>
      Array.from(compactRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? []);
    const list = items();
    const current = list.find((el) => el.getAttribute("aria-checked") === "true");
    (current ?? list[0])?.focus();
    const onPointerDown = (e: PointerEvent) => {
      if (compactRef.current && !compactRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const list = items();
      if (list.length === 0) return;
      e.preventDefault();
      const at = list.findIndex((el) => el === document.activeElement);
      const next =
        e.key === "ArrowDown" ? (at + 1) % list.length : (at - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="study-viewbar">
      {/* 狭い画面用。広い画面では CSS で隠す */}
      <div className="study-viewbar-compact" ref={compactRef}>
        <button
          ref={buttonRef}
          type="button"
          className="study-viewbar-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="study-visually-hidden">表示を切り替える。いまは</span>
          <span aria-hidden="true" className="study-viewbar-btn-icon">
            {selected.icon}
          </span>
          <span className="study-viewbar-btn-label">{selected.label}</span>
          <span aria-hidden="true" className="study-viewbar-caret" />
        </button>
        {open ? (
          <div className="study-viewbar-menu" role="menu" aria-label="表示を切り替える">
            {views.map((v) => (
              <button
                key={v.id}
                type="button"
                role="menuitemradio"
                aria-checked={v.id === selectedId}
                className="study-viewbar-menu-item"
                onClick={() => {
                  setOpen(false);
                  onSelect(v.id);
                }}
              >
                <span aria-hidden="true">{v.icon}</span>
                {v.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {/* 広い画面用。狭い画面では CSS で隠す */}
      <div className="study-views" role="tablist" aria-label="いちごスタディの表示">
        {views.map((v) => (
          <button
            key={v.id}
            type="button"
            role="tab"
            aria-selected={v.id === selectedId}
            id={`study-view-tab-${v.id}`}
            aria-controls={`study-view-panel-${v.id}`}
            className="study-view-tab"
            onClick={() => onSelect(v.id)}
          >
            <span aria-hidden="true">{v.icon}</span>
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}
